/**
 * Helpers for detecting skill name collisions and proposing a "Keep both"
 * rename. Shared by the create, zip-upload, and restore-from-trash flows so all
 * of them surface the same collision UX.
 *
 * Error shapes are messy here: the dashboard's axios response interceptor
 * flattens AxiosError into `ApiError` (status/body on `.status`/`.data`), the
 * api-client's `restore` can throw a typed `SkillNameTakenError`, and a raw
 * AxiosError (status/body under `.response`) can still slip through. These
 * helpers accept all three.
 */

/** Backend 409 collision body from the restore endpoint. */
type NameTakenBody = { error?: unknown; suggested_name?: unknown };

/** Standard Zensei error envelope (`{ error: { message } }`). */
type EnvelopeBody = { error?: { message?: unknown } };

type MaybeApiError = {
  name?: unknown;
  /** `SkillNameTakenError.suggestedName`. */
  suggestedName?: unknown;
  /** Set by `ApiError` (post-interceptor). */
  status?: number;
  data?: NameTakenBody | EnvelopeBody;
  /** Set by a raw AxiosError. */
  response?: { status?: number; data?: NameTakenBody | EnvelopeBody };
  message?: unknown;
};

function asRecord(err: unknown): MaybeApiError | null {
  if (typeof err !== "object" || err === null) return null;
  return err as MaybeApiError;
}

function statusOf(e: MaybeApiError): number | undefined {
  if (typeof e.status === "number") return e.status;
  if (typeof e.response?.status === "number") return e.response.status;
  return undefined;
}

/** The parsed 409 body, whether flattened onto `.data` or left under `.response.data`. */
function bodyOf(e: MaybeApiError): NameTakenBody | EnvelopeBody | undefined {
  return e.data ?? e.response?.data;
}

const COLLISION_MESSAGE_RE = /(already exists|same name)/i;

/**
 * The dash-suffixed name the backend suggests on a 409 `name_taken` (restore
 * endpoint), or null if the error isn't a structured name collision. Used by
 * the restore flow, which gets its suggestion from the server.
 */
export function nameTakenSuggestion(err: unknown): string | null {
  const e = asRecord(err);
  if (!e) return null;
  // An empty suggestion is unusable (the "Keep both" rename would send a blank
  // new_name and just re-collide), so treat it as "no suggestion" → null.
  if (
    e.name === "SkillNameTakenError" &&
    typeof e.suggestedName === "string" &&
    e.suggestedName !== ""
  ) {
    return e.suggestedName;
  }
  if (statusOf(e) === 409) {
    const body = bodyOf(e) as NameTakenBody | undefined;
    if (
      body?.error === "name_taken" &&
      typeof body.suggested_name === "string" &&
      body.suggested_name !== ""
    ) {
      return body.suggested_name;
    }
  }
  return null;
}

/**
 * True when an error is a skill name collision. Handles the restore endpoint's
 * structured `name_taken` body, the create endpoint's plain 409 ("A skill named
 * … already exists"), and the typed `SkillNameTakenError`. Any 409 from a skill
 * create/restore is, in practice, a name collision, so a bare 409 also counts.
 */
export function isSkillNameTaken(err: unknown): boolean {
  const e = asRecord(err);
  if (!e) return false;
  if (e.name === "SkillNameTakenError") return true;

  const body = bodyOf(e);
  if (body && typeof body === "object") {
    if ((body as NameTakenBody).error === "name_taken") return true;
    const msg = (body as EnvelopeBody).error?.message;
    if (typeof msg === "string" && COLLISION_MESSAGE_RE.test(msg)) return true;
  }
  if (typeof e.message === "string" && COLLISION_MESSAGE_RE.test(e.message)) {
    return true;
  }
  return statusOf(e) === 409;
}

/**
 * A unique skill name derived from `base` by appending `-1`, `-2`, … until one
 * is free. Mirrors the backend restore suggestion convention so the create and
 * restore flows propose names the same way.
 */
export function generateUniqueSkillName(
  existingNames: Iterable<string>,
  base: string,
): string {
  const taken = new Set(existingNames);
  if (!taken.has(base)) return base;
  for (let i = 1; i < 1000; i++) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base}-${Date.now()}`;
}
